import type { WidgetId } from '../../selfOwnedWidget';
import type { WidgetContainerController } from '../WidgetContainer';
import { computed, createModel } from '@preact/signals-react';
import type { Model, ModelConstructor, ReadonlySignal } from '@preact/signals-react';
import type { EventBus } from 'src/types/EventBus/EventBus';
import { EventBusBuilder } from 'src/types/EventBus/EventBus';
import type { ChannelChangeEvent, ChannelSignal, ResetChannelDataEvent } from 'src/types/EventBus/index';

export type WidgetContainerChannelSliceEventBus = EventBus<ChannelSignal, ChannelChangeEvent & ResetChannelDataEvent>;

interface WidgetContainerChannelSliceInit {
    container: WidgetContainerController;
    tabId: WidgetId;
    onResetChannelData?: () => void;
}

export interface WidgetContainerChannelSliceModel {
    readonly channel: ReadonlySignal<string | null>;
    readonly eventBus: WidgetContainerChannelSliceEventBus;
}

export const WidgetContainerChannelSlice: ModelConstructor<
    WidgetContainerChannelSliceModel,
    [WidgetContainerChannelSliceInit]
> = createModel<WidgetContainerChannelSliceModel, [WidgetContainerChannelSliceInit]>(
    (init: WidgetContainerChannelSliceInit) => {
        const channel: ReadonlySignal<string | null> = computed(() => {
            const tab = init.container.tabs.value.find((t) => t.id === init.tabId);
            const channelId = (tab?.widget as { channelId?: string | null } | undefined)?.channelId;
            return channelId ?? null;
        });

        const eventBus: WidgetContainerChannelSliceEventBus = EventBusBuilder.create()
            .addSignal('widget::channel', () => channel)
            .addEvent('widget::channelChange', ({ channel: nextChannel }) => {
                init.container.setTabChannel(init.tabId, nextChannel);
            })
            .addEvent('widget::resetChannelData', () => {
                init.onResetChannelData?.();
                init.container.resetTabChannelData(init.tabId);
            })
            .build();

        return { channel, eventBus };
    },
);

export type WidgetContainerChannelSlice = Model<WidgetContainerChannelSliceModel>;
